import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

interface RecentReferral {
  id: number;
  first_name: string;
  last_name: string;
  created_at: string;
  reward_cents: number | null;
  reward_status: string | null;
}

interface RewardTier {
  minReferrals: number;
  rewardCents: number;
  label: string;
}

interface TierInfo {
  current: RewardTier;
  next: RewardTier | null;
  referralsToNext: number;
}

interface RewardSummary {
  totalEarnedCents: number;
  pendingCents: number;
  paidCents: number;
  pendingCount: number;
  paidCount: number;
}

interface ReferralStats {
  code: string;
  shareUrl: string;
  totalReferrals: number;
  monthReferrals: number;
  recentReferrals: RecentReferral[];
  tier: TierInfo;
  rewards: RewardSummary;
  rewardTiers: RewardTier[];
}

interface ReferralState {
  stats: ReferralStats | null;
  loading: boolean;
  error: string | null;
}

const initialState: ReferralState = {
  stats: null,
  loading: false,
  error: null,
};

export const fetchReferralStats = createAsyncThunk(
  'referrals/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/referrals/stats');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to load referral data');
      }
      return rejectWithValue('Failed to load referral data');
    }
  }
);

const referralSlice = createSlice({
  name: 'referrals',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReferralStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReferralStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchReferralStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default referralSlice.reducer;
